/**
 * Frontend: Latest Publication sub-block.
 *
 * Click on card → full-page takeover: hides all page siblings,
 * shows inline subpage detail, updates URL with ?pub= parameter.
 * Back button → restores everything.
 */
document.querySelectorAll( '.br-latest-pub' ).forEach( ( block ) => {

	const listing  = block.querySelector( '[data-latest-pub-listing]' );
	const subpage  = block.querySelector( '[data-latest-pub-subpage]' );
	const cardBtn  = block.querySelector( '[data-latest-pub-open]' );
	const backBtn  = block.querySelector( '[data-latest-pub-back]' );
	const restUrl  = block.dataset.restUrl || '';
	const nonce    = block.dataset.nonce || '';

	if ( ! listing || ! subpage || ! cardBtn ) return;

	/* Subpage elements */
	const spTitle     = subpage.querySelector( '[data-subpage-title]' );
	const spHeading   = subpage.querySelector( '[data-subpage-heading]' );
	const spYear      = subpage.querySelector( '[data-subpage-year]' );
	const spSector    = subpage.querySelector( '[data-subpage-sector]' );
	const spType      = subpage.querySelector( '[data-subpage-type]' );
	const spDesc      = subpage.querySelector( '[data-subpage-desc]' );
	const spCover     = subpage.querySelector( '[data-subpage-cover]' );
	const spDownloads = subpage.querySelector( '[data-subpage-downloads]' );
	const spPreview   = subpage.querySelector( '[data-subpage-preview]' );
	const spEditions  = subpage.querySelector( '[data-subpage-editions]' );
	const spEdList    = subpage.querySelector( '[data-subpage-editions-list]' );

	const HIDDEN_CLASS = 'br-latest-pub-hidden';

	/* ═══════════════════════════════════════
	   PAGE TAKEOVER – hide/show siblings
	   ═══════════════════════════════════════ */

	let hiddenElements = [];

	/**
	 * Walk up the DOM from the block, hiding only siblings
	 * that come AFTER it at each level. Header/nav above
	 * and footer below remain visible.
	 */
	const hidePageSiblings = () => {
		hiddenElements = [];
		let el = block;

		while ( el && el !== document.body ) {
			let sibling = el.nextElementSibling;
			while ( sibling ) {
				const tag = sibling.tagName;
				const isProtected = tag === 'SCRIPT' || tag === 'STYLE' || tag === 'LINK' || tag === 'FOOTER'
					|| ( sibling.id && /footer/i.test( sibling.id ) )
					|| /footer/i.test( sibling.className || '' )
					|| sibling.getAttribute( 'role' ) === 'contentinfo';

				if ( ! isProtected && ! sibling.classList.contains( HIDDEN_CLASS ) ) {
					sibling.classList.add( HIDDEN_CLASS );
					hiddenElements.push( sibling );
				}
				sibling = sibling.nextElementSibling;
			}
			el = el.parentElement;
		}
	};

	const showPageSiblings = () => {
		hiddenElements.forEach( ( el ) => el.classList.remove( HIDDEN_CLASS ) );
		hiddenElements = [];
	};

	/* ═══════════════════════════════════════
	   SHARED HELPERS (mirrors parent view.js)
	   ═══════════════════════════════════════ */

	const LANG_LABELS = { E: 'English', F: 'Français', S: 'Español', A: 'العربية', C: '中文', R: 'Русский' };
	const LANG_COLORS = { E: '#0076a1', F: '#2b71b1', S: '#d4a017', A: '#217346', C: '#c0392b', R: '#8e44ad' };
	const LANG_ICON_LABELS = { E: 'EN', F: 'FR', S: 'ES', A: 'AR', C: 'ZH', R: 'RU' };
	const FILE_TYPE_COLORS = {
		pdf: '#c0392b', zip: '#f39c12', rar: '#f39c12', '7z': '#f39c12',
		doc: '#2b71b1', docx: '#2b71b1', xls: '#217346', xlsx: '#217346',
		csv: '#217346', ppt: '#d24726', pptx: '#d24726', epub: '#8e44ad',
		mobi: '#e67e22', htm: '#e44d26', html: '#e44d26',
	};

	const HASH_TO_LABEL = { fr: 'Français', ar: 'العربية', zh: '中文', ru: 'Русский', es: 'Español', en: 'English' };

	const getPageLangLabel = () => {
		const hash = window.location.hash;
		const match = hash.match( /^#\/?([a-z]{2})/ );
		return match ? ( HASH_TO_LABEL[ match[1] ] || 'English' ) : 'English';
	};

	const getDlLabel = ( dl ) => dl.lang && LANG_LABELS[ dl.lang ] ? LANG_LABELS[ dl.lang ] : ( dl.label || 'Document' );

	const PREVIEWABLE = new Set( [ 'pdf', 'htm', 'html', 'doc', 'docx' ] );
	const PREVIEW_PRIORITY = { pdf: 0, doc: 1, docx: 1, htm: 2, html: 2 };

	const detectFileType = ( dl ) => {
		const url = ( dl.url || '' ).toLowerCase();
		const label = ( dl.label || '' ).toLowerCase();
		const pathMatch = url.match( /\.([a-z0-9]{2,5})(?:[?#]|$)/i );
		if ( pathMatch ) {
			const ext = pathMatch[1];
			if ( [ 'pdf','zip','rar','7z','doc','docx','xls','xlsx','ppt','pptx','csv','txt','xml','epub','mobi','htm','html' ].includes( ext ) ) return ext;
		}
		const formatMatch = url.match( /[?&](?:format|type|ext|filetype)=([a-z0-9]+)/i );
		if ( formatMatch ) return formatMatch[1];
		if ( /\.pdf|\/pdf|pdf$/i.test( label ) || /\.pdf|\/pdf/i.test( url ) ) return 'pdf';
		if ( /\.zip|\/zip|zip$/i.test( label ) || /\.zip|\/zip/i.test( url ) ) return 'zip';
		if ( /\.docx?\b/i.test( label ) || /\/docx?\b/i.test( url ) ) return 'docx';
		return 'pdf';
	};

	const canPreview = ( dl ) => PREVIEWABLE.has( detectFileType( dl ) );

	const pickBestPreview = ( downloads ) => {
		const targetLabel = getPageLangLabel();
		const previewable = ( downloads || [] ).filter( canPreview );
		if ( ! previewable.length ) return null;
		const sorted = [ ...previewable ].sort( ( a, b ) =>
			( PREVIEW_PRIORITY[ detectFileType( a ) ] ?? 9 ) - ( PREVIEW_PRIORITY[ detectFileType( b ) ] ?? 9 )
		);
		return sorted.find( dl => getDlLabel( dl ) === targetLabel )
			|| sorted.find( dl => getDlLabel( dl ) === 'English' )
			|| sorted[0];
	};

	const buildPreviewSrc = ( url, ext, zoom ) => {
		if ( ext === 'pdf' ) return `${ url }#toolbar=0&navpanes=0&zoom=${ zoom }`;
		if ( ext === 'htm' || ext === 'html' ) return url;
		if ( ext === 'doc' || ext === 'docx' ) return `https://view.officeapps.live.com/op/embed.aspx?src=${ encodeURIComponent( url ) }`;
		return url;
	};

	const DOC_SVG_WITH_LABEL = ( label, color ) =>
		`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke-width="1.5">
			<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="${ color }"/>
			<polyline points="14 2 14 8 20 8" stroke="${ color }"/>
			<text x="12" y="17.5" text-anchor="middle" font-size="${ label.length > 3 ? 4.5 : label.length > 2 ? 5.5 : 6 }" font-weight="700" fill="${ color }" font-family="Arial,sans-serif" stroke="none">${ label }</text>
		</svg>`;

	const getFileIcon = ( dl ) => {
		const ext = detectFileType( dl );
		if ( dl.lang && LANG_COLORS[ dl.lang ] ) {
			const lbl = LANG_ICON_LABELS[ dl.lang ] || dl.lang;
			return DOC_SVG_WITH_LABEL( lbl, LANG_COLORS[ dl.lang ] );
		}
		return DOC_SVG_WITH_LABEL( ext.toUpperCase(), FILE_TYPE_COLORS[ ext ] || '#0076a1' );
	};

	const buildDownloadsHTML = ( downloads ) => {
		if ( ! downloads || downloads.length === 0 ) {
			return '<p class="br-pub-modal__no-dl">No downloads available for this publication.</p>';
		}
		const initialDl = pickBestPreview( downloads );
		let html = '<div class="br-pub-dl-cards">';
		downloads.forEach( dl => {
			const label = getDlLabel( dl );
			const icon = getFileIcon( dl );
			const ext = detectFileType( dl );
			const previewOk = canPreview( dl );
			const previewAttr = previewOk ? ` data-preview-url="${ dl.url }" data-preview-ext="${ ext }"` : '';
			const isInitial = previewOk && initialDl && dl.url === initialDl.url;
			const cls = 'br-pub-dl-card' + ( isInitial ? ' is-previewing' : '' );
			html += `<a href="${ dl.url }" download class="${ cls }"${ previewAttr }>
				<div class="br-pub-dl-card__icon">${ icon }</div>
				<span class="br-pub-dl-card__label">${ label }</span>
				<span class="br-pub-dl-card__price">Free of charge</span>
				<div class="br-pub-dl-card__dl-icon">
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#0076a1" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
				</div>
			</a>`;
		} );
		html += '</div>';
		return html;
	};

	const LOADING_HTML = '<div class="br-pub-modal__loading"><div class="br-pub__spinner-small"></div><span>Loading…</span></div>';

	const VIEWPORT_EMPTY_HTML = `<div class="br-pub-preview__empty">
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5">
			<path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
			<line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
		</svg>
		<span>Hover a document to preview</span>
	</div>`;

	const resetPreview = ( previewEl ) => {
		if ( ! previewEl ) return;
		const header   = previewEl.querySelector( '[data-preview-header]' );
		const viewport = previewEl.querySelector( '[data-preview-viewport]' );
		if ( header )   header.style.display = 'none';
		if ( viewport ) viewport.innerHTML = VIEWPORT_EMPTY_HTML;
	};

	/* ─── Preview ─── */

	const ZOOM_STEPS  = [ 50, 60, 70, 80, 90, 100, 125, 150 ];
	const DEFAULT_ZOOM = 80;

	const setupPreview = ( downloadsEl, previewEl, downloads ) => {
		if ( ! previewEl ) return;
		const header    = previewEl.querySelector( '[data-preview-header]' );
		const viewport  = previewEl.querySelector( '[data-preview-viewport]' );
		const titleEl   = previewEl.querySelector( '[data-preview-title]' );
		const levelEl   = previewEl.querySelector( '[data-preview-zoom-level]' );
		const zoomOutEl = previewEl.querySelector( '[data-preview-zoom="out"]' );
		const zoomInEl  = previewEl.querySelector( '[data-preview-zoom="in"]' );
		const zoomWrap  = previewEl.querySelector( '.br-pub-preview__zoom' );
		if ( ! viewport ) return;

		let currentUrl = '', currentExt = 'pdf', currentLabel = '', zoom = DEFAULT_ZOOM;

		const loadIframe = ( url, ext, z ) => {
			viewport.innerHTML = `<iframe class="br-pub-preview__iframe" src="${ buildPreviewSrc( url, ext, z ) }" title="Document preview"></iframe>`;
		};
		const updateZoomLabel = () => { if ( levelEl ) levelEl.textContent = zoom + ' %'; };

		const showPreview = ( url, ext, label ) => {
			const urlChanged = url !== currentUrl;
			currentUrl = url; currentExt = ext || 'pdf'; currentLabel = label || currentLabel;
			if ( header ) header.style.display = '';
			if ( titleEl ) titleEl.textContent = currentLabel + ' — preview';
			if ( zoomWrap ) zoomWrap.style.display = currentExt === 'pdf' ? '' : 'none';
			if ( urlChanged ) { zoom = DEFAULT_ZOOM; updateZoomLabel(); loadIframe( url, currentExt, zoom ); }
		};

		const changeZoom = ( dir ) => {
			if ( currentExt !== 'pdf' ) return;
			const idx = ZOOM_STEPS.indexOf( zoom );
			let next;
			if ( dir === 'in' ) next = idx < ZOOM_STEPS.length - 1 ? ZOOM_STEPS[ idx + 1 ] : zoom;
			else next = idx > 0 ? ZOOM_STEPS[ idx - 1 ] : zoom;
			if ( next === zoom ) return;
			zoom = next; updateZoomLabel();
			if ( currentUrl ) loadIframe( currentUrl, currentExt, zoom );
		};

		if ( zoomOutEl ) zoomOutEl.addEventListener( 'click', ( e ) => { e.preventDefault(); e.stopPropagation(); changeZoom( 'out' ); } );
		if ( zoomInEl )  zoomInEl.addEventListener( 'click', ( e ) => { e.preventDefault(); e.stopPropagation(); changeZoom( 'in' ); } );

		const initialDl = pickBestPreview( downloads );
		if ( initialDl ) {
			showPreview( initialDl.url, detectFileType( initialDl ), getDlLabel( initialDl ) );
		}

		downloadsEl.querySelectorAll( '[data-preview-url]' ).forEach( card => {
			card.addEventListener( 'mouseenter', () => {
				downloadsEl.querySelectorAll( '.br-pub-dl-card' ).forEach( c => c.classList.remove( 'is-previewing' ) );
				card.classList.add( 'is-previewing' );
				const lbl = card.querySelector( '.br-pub-dl-card__label' );
				showPreview( card.dataset.previewUrl, card.dataset.previewExt || 'pdf', lbl ? lbl.textContent : '' );
			} );
		} );
	};

	const buildEditionButtons = ( editions, container, className ) => {
		container.innerHTML = '';
		if ( ! editions || editions.length === 0 ) return false;
		editions.forEach( ed => {
			const btn = document.createElement( 'button' );
			btn.type = 'button'; btn.className = className;
			btn.textContent = ed.year; btn.dataset.editionUrl = ed.url;
			container.appendChild( btn );
		} );
		return true;
	};

	const fetchDetail = ( link ) => {
		if ( ! restUrl || ! link ) return Promise.reject();
		return fetch( `${ restUrl }?url=${ encodeURIComponent( link ) }`, { headers: { 'X-WP-Nonce': nonce } } )
			.then( r => r.json() );
	};

	/* ═══════════════════════════════════════
	   OPEN / CLOSE SUBPAGE
	   ═══════════════════════════════════════ */

	const openSubpage = ( data, { pushHistory = true } = {} ) => {
		if ( spTitle )   spTitle.textContent   = data.title;
		if ( spHeading ) spHeading.textContent = data.title;
		if ( spYear )    spYear.textContent    = data.date ? data.date.substring( 0, 4 ) : '';
		if ( spSector )  spSector.textContent  = data.sector || '';
		if ( spType )    spType.textContent    = data.type || '';
		if ( spDesc )    spDesc.textContent    = data.desc || '';
		if ( spCover )   spCover.innerHTML     = '<div class="br-pub-modal__cover-placeholder"></div>';
		if ( spDownloads ) spDownloads.innerHTML = LOADING_HTML;
		if ( spPreview ) resetPreview( spPreview );
		if ( spEditions ) spEditions.style.display = 'none';
		if ( spEdList )   spEdList.innerHTML = '';

		/* Hide listing + all page siblings */
		listing.style.display = 'none';
		subpage.style.display = '';
		block.classList.add( 'br-latest-pub--subpage-open' );
		hidePageSiblings();

		/* Update URL */
		if ( pushHistory && data.link ) {
			const url = new URL( window.location );
			url.searchParams.set( 'pub', data.link );
			history.pushState( { brLatestPubDetail: true, data }, '', url );
		}

		window.scrollTo( { top: 0, behavior: 'smooth' } );

		fetchDetail( data.link )
			.then( detail => {
				if ( detail.cover && spCover ) spCover.innerHTML = `<img src="${ detail.cover }" alt="${ data.title }" />`;
				if ( spDownloads ) {
					spDownloads.innerHTML = buildDownloadsHTML( detail.downloads );
					setupPreview( spDownloads, spPreview, detail.downloads );
				}
				if ( spEdList && buildEditionButtons( detail.editions, spEdList, 'br-pub-modal__edition-btn' ) ) {
					spEditions.style.display = 'flex';
				}
			} )
			.catch( () => {
				if ( spDownloads ) spDownloads.innerHTML = '<p class="br-pub-modal__no-dl">Could not load publication details.</p>';
				if ( spPreview ) resetPreview( spPreview );
			} );
	};

	const closeSubpage = ( { updateUrl = true } = {} ) => {
		subpage.style.display = 'none';
		listing.style.display = '';
		block.classList.remove( 'br-latest-pub--subpage-open' );
		showPageSiblings();

		if ( updateUrl ) {
			const url = new URL( window.location );
			url.searchParams.delete( 'pub' );
			history.pushState( null, '', url );
		}

		block.scrollIntoView( { behavior: 'smooth', block: 'start' } );
	};

	/* ─── Events ─── */

	cardBtn.addEventListener( 'click', () => {
		openSubpage( {
			title:  cardBtn.dataset.pubTitle,
			desc:   cardBtn.dataset.pubDesc,
			link:   cardBtn.dataset.pubLink,
			date:   cardBtn.dataset.pubDate,
			sector: cardBtn.dataset.pubSector,
			type:   cardBtn.dataset.pubType,
		} );
	} );

	if ( backBtn ) {
		backBtn.addEventListener( 'click', () => {
			history.back();
		} );
	}

	/* Browser back/forward */
	window.addEventListener( 'popstate', ( e ) => {
		if ( e.state && e.state.brLatestPubDetail ) {
			openSubpage( e.state.data, { pushHistory: false } );
		} else if ( block.classList.contains( 'br-latest-pub--subpage-open' ) ) {
			closeSubpage( { updateUrl: false } );
		}
	} );

	/* Edition buttons */
	if ( spEdList ) {
		spEdList.addEventListener( 'click', ( e ) => {
			const btn = e.target.closest( '[data-edition-url]' );
			if ( ! btn ) return;
			openSubpage( {
				title:  spHeading ? spHeading.textContent : '',
				desc:   '',
				link:   btn.dataset.editionUrl,
				date:   btn.textContent,
				sector: '',
				type:   '',
			} );
		} );
	}

	/* ═══════════════════════════════════════
	   AUTO-OPEN from ?pub= URL parameter
	   ═══════════════════════════════════════ */

	const initUrl = new URL( window.location );
	const initPub = initUrl.searchParams.get( 'pub' );

	if ( initPub ) {
		const data = {
			title:  cardBtn.dataset.pubTitle  || '',
			desc:   cardBtn.dataset.pubDesc   || '',
			link:   initPub,
			date:   cardBtn.dataset.pubDate   || '',
			sector: cardBtn.dataset.pubSector || '',
			type:   cardBtn.dataset.pubType   || '',
		};

		openSubpage( data, { pushHistory: false } );
		history.replaceState( { brLatestPubDetail: true, data }, '', window.location );
	}

	/* ═══════════════════════════════════════
	   REACT TO PARENT PUBLICATIONS BLOCK
	   Hide listing when parent opens its subpage,
	   show it again when parent closes it.
	   ═══════════════════════════════════════ */

	const pubBlocks = document.querySelectorAll( '.br-pub' );

	pubBlocks.forEach( ( pubBlock ) => {
		const observer = new MutationObserver( () => {
			if ( pubBlock.classList.contains( 'br-pub--subpage-open' ) ) {
				listing.style.display = 'none';
			} else if ( ! block.classList.contains( 'br-latest-pub--subpage-open' ) ) {
				listing.style.display = '';
			}
		} );

		observer.observe( pubBlock, { attributes: true, attributeFilter: [ 'class' ] } );
	} );

} );